<template>
    <div>
        <AddNewMaterialDialog
            :is-open="addNewMaterialDialog"
            @on-confirm="saveMaterial"
            @on-close="closeNewMaterialDialog"
        />
        <v-dialog
            :value="isOpen"
            max-width="1200px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="action === 'updateMaterial'">
                            Edycja materiału
                        </template>
                        <template v-else>
                            Dodawanie materiału
                        </template>
                    </h1>
                </v-card-title>
                <v-card-text class="text-center">
                    <v-container fluid class="pa-0">
                        <v-row>
                            <v-col cols="12">
                                <v-radio-group v-model="radioGroup" row>
                                    <v-radio
                                        value="1"
                                        label="Papiery"
                                    />
                                    <v-radio
                                        value="2"
                                        label="Materiały"
                                    />
                                </v-radio-group>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" lg="4">
                                <v-autocomplete
                                    v-model="material.materialName"
                                    :items="radioGroup === '1' ? papers : materials"
                                    :filter="customFilter"
                                    item-text="name"
                                    item-value="name"
                                    value="request.materials"
                                    no-data-text="Nie znaleziono materiałów"
                                    :label="radioGroup === 1 ? 'Papier' : 'Materiał'"
                                    clearable
                                    chips
                                    deletable-chips
                                    :error-messages="requestMaterialNameErrors"
                                    required
                                >
                                    <template #selection="data">
                                        <v-chip
                                            close
                                            @update:active="removeMaterialName(data.item)"
                                        >
                                            {{ data.item.name }}
                                        </v-chip>
                                    </template>

                                    <template #item="data">
                                        {{ data.item.name }}
                                    </template>
                                    <template slot="append-outer">
                                        <v-tooltip top :open-delay="tooltipDelay">
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Dodaj materiał"
                                                    v-on="on"
                                                    @click="showMaterialDialog"
                                                >
                                                    <v-icon color="primary">
                                                        mdi-plus
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Dodaj materiał</span>
                                        </v-tooltip>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="12" lg="3" class="mt-2">
                                <v-text-field
                                    v-model="material.materialFormat"
                                    label="Format materiału"
                                    :error-messages="requestMaterialFormatErrors"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" lg="3" class="mt-2">
                                <v-text-field
                                    v-model="material.materialWeight"
                                    label="Ilość materiału"
                                    :error-messages="materialWeightErrors"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" lg="2" class="mt-2">
                                <v-select
                                    v-model="material.unit"
                                    :items="unitsOfMeasure"
                                    item-value="name"
                                    label="ark/kg"
                                    :error-messages="unitErrors"
                                >
                                    <template slot="selection" slot-scope="data">
                                        {{ data.item.name }}
                                    </template>

                                    <template slot="item" slot-scope="data">
                                        {{ data.item.name }}
                                    </template>
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-row v-if="hasEditPermission">
                            <v-col sm="auto">
                                <v-checkbox
                                    v-model="material.atStock"
                                    label="W magazynie"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="12" lg="4">
                                <v-text-field
                                    v-model="material.externalRequestNumber"
                                    label="Numer zlecenia"
                                    required
                                    :disabled="material.atStock"
                                    :error-messages="materialExternalRequestNumberErrors"
                                />
                            </v-col>
                            <v-col cols="12" lg="4">
                                <v-text-field
                                    v-model="material.providerName"
                                    label="Nazwa dostawcy"
                                    required
                                    :disabled="material.atStock"
                                    :error-messages="providerNameErrors"
                                />
                            </v-col>
                            <v-col>
                                <v-menu
                                    v-model="deliveryDate"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px"
                                >
                                    <template #activator="{on}">
                                        <v-text-field
                                            v-model="material.deliveryDate"
                                            label="Data dostawy"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            clearable
                                            :disabled="material.atStock"
                                            :error-messages="deliveryDateErrors"
                                            v-on="on"
                                        />
                                    </template>
                                    <v-date-picker
                                        v-model="material.deliveryDate"
                                        first-day-of-week="1"
                                        @input="deliveryDate = false"
                                    />
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose()"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="addMaterial()"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { required, numeric, requiredIf } from 'vuelidate/lib/validators'
import AddNewMaterialDialog from './AddNewMaterialDialog'
import tooltipDelay from '@/mixins/tooltip'
import customFilters from '@/mixins/customFilters'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'AddMaterialDialog',
    components: {
        AddNewMaterialDialog
    },
    mixins: [ tooltipDelay, customFilters ],
    props: {
        isOpen: {
            type: Boolean
        },
        request: {
            type: Object,
            default: null
        },
        material: {
            type: Object,
            default: null
        },
        validations: {
            type: Object,
            default: null
        },
        action: {
            type: String,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        materials: {
            type: Array,
            default: null
        },
    },
    data() {
        return {
            deliveryDate: false,
            actionLoading: false,
            addNewMaterialDialog: false,
            excludedMaterialIds: [1, 4, 5], //szt, m, m2
            radioGroup: '1'
        }
    },
    validations: {
        material: {
            materialName: {
                required
            },
            materialWeight: {
                required,
                numeric
            },
            unit: {
                required
            },
            materialFormat: {
                required
            },
            externalRequestNumber: {
                required: requiredIf(function(material) {
                    return !material.atStock && this.request.statusId === 40 && this.request.requestTypeId === 1
                })
            },
            providerName: {
                required: requiredIf(function(material) {
                    return !material.atStock && this.request.statusId === 40 && this.request.requestTypeId === 1
                })
            },
            deliveryDate: {
                required: requiredIf(function(material) {
                    return !material.atStock && this.request.statusId === 40 && this.request.requestTypeId === 1
                })
            }
        }
    },
    computed: {
        unitsOfMeasure() {
            return this.$store.state.configurations.materials.unitsOfMeasure.filter(item => !this.excludedMaterialIds.includes(item.id))
        },
        requestMaterialNameErrors() {
            const errors = []

            if (!this.$v.material.materialName.$dirty) return errors
            !this.$v.material.materialName.required && errors.push('Nazwa materiału jest wymagana.')

            return errors
        },
        requestMaterialFormatErrors() {
            const errors = []

            if (!this.$v.material.materialFormat.$dirty) return errors
            !this.$v.material.materialFormat.required && errors.push('Format materiału jest wymagany.')

            return errors
        },
        materialWeightErrors() {
            const errors = []

            if (!this.$v.material.materialWeight.$dirty) return errors
            !this.$v.material.materialWeight.required && errors.push('Waga jest wymagana.')
            !this.$v.material.materialWeight.numeric && errors.push('Niepoprawny format.')

            return errors
        },
        unitErrors() {
            const errors = []

            if (!this.$v.material.unit.$dirty) return errors
            !this.$v.material.unit.required && errors.push('Jednostka jest wymagana.')

            return errors
        },
        materialExternalRequestNumberErrors() {
            const errors = []

            if (!this.$v.material.externalRequestNumber.$dirty) return errors
            !this.$v.material.externalRequestNumber.required && errors.push('Numer zlecenia jest wymagany.')

            return errors
        },
        providerNameErrors() {
            const errors = []

            if (!this.$v.material.providerName.$dirty) return errors
            !this.$v.material.providerName.required && errors.push('Nazwa dostawcy jest wymagana.')

            return errors
        },
        deliveryDateErrors() {
            const errors = []

            if (!this.$v.material.deliveryDate.$dirty) return errors
            !this.$v.material.deliveryDate.required && errors.push('Data dostawy jest wymagana.')

            return errors
        },
        hasEditPermission() {
            return hasUserPermission('requests_update')
        },
    },
    methods: {
        updateRadioGroup(material) {
            this.radioGroup = this.materials.some(item => item.name === material.materialName) ? '2' : '1'
        },
        addMaterial() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading = true
            if (this.action === 'createMaterial') {
                let item = Object.assign({}, this.material)

                this.$store.dispatch('purchases/updateMaterialsList', item)
            }
            if (this.action === 'updateMaterial') {
                let item = Object.assign({}, this.material)

                this.$store.dispatch('purchases/updateMaterial', item)
            }
            this.onClose()
        },
        removeMaterialName() {
            this.material.materialName = null
        },
        saveMaterial(material) {
            this.$emit('update-material-name', material)
        },
        showMaterialDialog() {
            this.addNewMaterialDialog = true
        },
        closeNewMaterialDialog() {
            this.addNewMaterialDialog = false
        },
        onClose() {
            this.actionLoading = false
            this.$v.$reset()
            this.$emit('on-close')
        },
    }
}
</script>

